import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  fetchAllCourses, 
  updatePublish, 
  deleteCourse as deleteCourseApi 
} from '../../../api/apiServices';

export default function AdminCoursesPage() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('All');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [publishingId, setPublishingId] = useState(null);

  // Notification system
  const [notification, setNotification] = useState({ 
    show: false, 
    type: '', 
    message: '' 
  });

  const levels = ['All', 'Beginner', 'Intermediate', 'Advanced'];

  // Show notification helper
  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => {
      setNotification({ show: false, type: '', message: '' });
    }, 4000);
  };

  // Fetch all courses
  const loadCourses = async () => {
    try {
      setLoading(true);
      const response = await fetchAllCourses();
      
      if (response.success) {
        setCourses(response.courses || []);
      } else {
        showNotification('error', response.message || "Failed to load courses");
      }
    } catch (err) {
      console.error("Failed to fetch courses:", err);
      showNotification('error', "Failed to load courses. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCourses();
  }, []);

  // Filter courses
  const filtered = courses.filter(c => {
    const matchSearch = 
      c.title?.toLowerCase().includes(search.toLowerCase()) ||
      c.instructor?.toLowerCase().includes(search.toLowerCase()) ||
      c.category?.toLowerCase().includes(search.toLowerCase());

    const matchLevel = levelFilter === 'All' || c.level === levelFilter;
    return matchSearch && matchLevel;
  });

  // Toggle Publish / Unpublish
  const handleTogglePublish = async (courseId, currentStatus) => {
    setPublishingId(courseId);
    setActionLoading(true);

    try {
      const response = await updatePublish(courseId);
      
      if (response.success) {
        setCourses(prev => 
          prev.map(course => 
            course._id === courseId 
              ? { ...course, isPublished: !currentStatus } 
              : course
          )
        );
        
        showNotification(
          'success', 
          `Course ${!currentStatus ? 'published' : 'unpublished'} successfully!`
        );
      } else {
        showNotification('error', response.message || "Failed to update status");
      }
    } catch (err) {
      console.error("Publish toggle failed:", err?.response?.data?.error);
      showNotification('error', err?.response?.data?.error);
    } finally {
      setPublishingId(null);
      setActionLoading(false);
    }
  };

  // Delete course
  const handleDelete = async (courseId) => {
    setActionLoading(true);
    
    try {
      await deleteCourseApi(courseId);
      
      setCourses(prev => prev.filter(c => c._id !== courseId));
      setDeleteConfirm(null);
      
      showNotification('success', 'Course deleted successfully');
    } catch (err) {
      console.error("Delete failed:", err);
      showNotification('error', err.response?.data?.message || "Failed to delete course");
    } finally {
      setActionLoading(false);
    }
  };

  // Loading State
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center">
          <i className="ri-loader-4-line animate-spin text-4xl text-teal-600 mb-4" />
          <p className="text-slate-500">Loading your courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 sm:px-6 lg:px-8 py-6">
      {/* Notification */}
      {notification.show && (
        <div className={`mx-auto max-w-7xl px-4 py-3 rounded-2xl border flex items-center gap-3 text-sm font-medium shadow-sm ${
          notification.type === 'success' 
            ? 'bg-emerald-50 border-emerald-200 text-emerald-700' 
            : 'bg-rose-50 border-rose-200 text-rose-700'
        }`}>
          <i className={`ri-${notification.type === 'success' ? 'check-circle' : 'error-warning'}-line text-xl flex-shrink-0`} />
          <span>{notification.message}</span>
        </div>
      )}

      {/* Summary Cards - Responsive Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Courses', value: courses.length, icon: 'ri-book-3-line', color: '#0d9488', bg: 'rgba(20,184,166,0.08)' },
          { label: 'Beginner', value: courses.filter(c => c.level === 'Beginner').length, icon: 'ri-seedling-line', color: '#10b981', bg: 'rgba(16,185,129,0.08)' },
          { label: 'Intermediate', value: courses.filter(c => c.level === 'Intermediate').length, icon: 'ri-bar-chart-line', color: '#f59e0b', bg: 'rgba(245,158,11,0.08)' },
          { label: 'Advanced', value: courses.filter(c => c.level === 'Advanced').length, icon: 'ri-award-line', color: '#ef4444', bg: 'rgba(239,68,68,0.08)' },
        ].map((s, index) => (
          <div key={index} className="bg-white rounded-2xl p-5 border border-slate-100 flex items-center gap-4">
            <div 
              className="w-11 h-11 flex items-center justify-center rounded-2xl text-2xl flex-shrink-0"
              style={{ background: s.bg, color: s.color }}
            >
              <i className={s.icon} />
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-900 tracking-tight">{s.value}</p>
              <p className="text-xs text-slate-500 mt-1">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Main Table Card */}
<div className="bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-sm">

  {/* Toolbar - Fixed Overflow */}
  <div className="px-4 sm:px-6 py-4 border-b border-slate-100">
    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
      
      {/* Search Input */}
      <div className="relative flex-1 w-full sm:max-w-md">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
          <i className="ri-search-line text-base" />
        </div>
        <input
          type="text"
          placeholder="Search courses..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm focus:outline-none focus:border-teal-500 transition-colors"
        />
      </div>

      {/* Level Filter */}
      <div className="flex flex-wrap gap-1 bg-slate-50 border border-slate-200 rounded-2xl p-1 w-full sm:w-auto">
        {levels.map((lvl) => (
          <button
            key={lvl}
            onClick={() => setLevelFilter(lvl)}
            className={`px-5 py-2 text-xs font-semibold rounded-xl transition-all whitespace-nowrap ${
              levelFilter === lvl
                ? 'bg-teal-600 text-white shadow-sm'
                : 'text-slate-600 hover:bg-white'
            }`}
          >
            {lvl}
          </button>
        ))}
      </div>

      {/* Right side info + New Course button */}
      <div className="flex items-center justify-between sm:justify-end gap-4 w-full sm:w-auto ml-auto">
        <div className="text-sm text-slate-500 whitespace-nowrap hidden sm:block">
          {filtered.length} of {courses.length} courses
        </div>

        <Link
          to="/admin/courses/new"
          className="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-2xl flex items-center gap-2 transition-all active:scale-95 whitespace-nowrap"
        >
          <i className="ri-add-line" /> New Course
        </Link>
      </div>
    </div>
  </div>

  {/* Table Container */}
  <div className="overflow-x-auto">
    {/* Desktop Table */}
    <table className="w-full min-w-[850px] hidden md:table">
      <thead>
        <tr className="bg-slate-50 border-b border-slate-100">
          <th className="text-left px-6 py-5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Course</th>
          <th className="text-left px-4 py-5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Category</th>
          <th className="text-left px-4 py-5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Level</th>
          <th className="text-left px-4 py-5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Students</th>
          <th className="text-left px-4 py-5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Price</th>
          <th className="text-left px-4 py-5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
          <th className="text-left px-6 py-5 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {filtered.map((course) => (
          <tr key={course._id} className="hover:bg-slate-50/70 transition-colors">
            <td className="px-6 py-5">
              <div className="flex items-center gap-4">
                <img
                  src={course.thumbnail || 'https://via.placeholder.com/300x170?text=No+Image'}
                  alt={course.title}
                  className="w-16 h-12 rounded-2xl object-cover border border-slate-200 flex-shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-900 line-clamp-1 pr-2">{course.title}</p>
                  <p className="text-sm text-slate-500 truncate">{course.instructor}</p>
                </div>
              </div>
            </td>
            <td className="px-4 py-5">
              <span className="text-xs px-3 py-1 rounded-full font-medium bg-teal-50 text-teal-700 capitalize">
                {course.category?.replace('-', ' ') || '—'}
              </span>
            </td>
            <td className="px-4 py-5">
              <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                course.level === 'Beginner' ? 'bg-emerald-100 text-emerald-700' :
                course.level === 'Intermediate' ? 'bg-amber-100 text-amber-700' :
                'bg-rose-100 text-rose-700'
              }`}>
                {course.level}
              </span>
            </td>
            <td className="px-4 py-5 text-sm font-medium text-slate-700">
              {course.studentsEnrolled?.toLocaleString() || 0}
            </td>
            <td className="px-4 py-5">
              <span className="font-bold text-slate-900">
                ₦{(course.price * 1400).toLocaleString()}
              </span>
            </td>
            <td className="px-4 py-5">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                course.isPublished ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
              }`}>
                <i className={`ri-${course.isPublished ? 'check-circle' : 'time-line'}`} />
                {/* {course.isPublished ? 'Published' : 'Draft'} */}
              </span>
            </td>
            <td className="px-2 py-5">
              <div className="flex items-center justify-end gap-2">
                <Link
                  to={`/admin/courses/${course._id}/edit`}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-xl bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors"
                >
                  <i className="ri-edit-2-line" /> Edit
                </Link>

                <button
                  onClick={() => handleTogglePublish(course._id, course.isPublished)}
                  disabled={publishingId === course._id || actionLoading}
                  className={`flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-xl transition-all ${
                    course.isPublished 
                      ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' 
                      : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                  }`}
                >
                  {publishingId === course._id ? (
                    <i className="ri-loader-4-line animate-spin" />
                  ) : course.isPublished ? (
                    <>Unpublish</>
                  ) : (
                    <>Publish</>
                  )}
                </button>

                <Link
                  to={`/courses/${course._id}`}
                  target="_blank"
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-600"
                >
                  <i className="ri-eye-line" />
                </Link>

                <button
                  onClick={() => setDeleteConfirm(course._id)}
                  className="w-9 h-9 flex items-center justify-center rounded-xl text-rose-600 hover:bg-rose-50 transition-colors"
                >
                  <i className="ri-delete-bin-6-line" />
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>

    {/* Mobile Card View */}
    <div className="md:hidden space-y-4 p-4">
      {filtered.map((course) => (
        <div key={course._id} className="bg-white border border-slate-200 rounded-3xl p-5 space-y-5">
          <div className="flex gap-4">
            <img
              src={course.thumbnail || 'https://via.placeholder.com/300x170?text=No+Image'}
              alt={course.title}
              className="w-20 h-14 rounded-2xl object-cover border border-slate-200 flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className="font-semibold leading-tight line-clamp-2">{course.title}</p>
              <p className="text-sm text-slate-500 mt-1 truncate">{course.instructor}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
            <div>
              <p className="text-xs text-slate-500">Category</p>
              <p className="font-medium capitalize mt-0.5">{course.category?.replace('-', ' ') || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Level</p>
              <span className={`text-xs px-3 py-1 rounded-full font-medium inline-block mt-0.5 ${
                course.level === 'Beginner' ? 'bg-emerald-100 text-emerald-700' :
                course.level === 'Intermediate' ? 'bg-amber-100 text-amber-700' :
                'bg-rose-100 text-rose-700'
              }`}>
                {course.level}
              </span>
            </div>
          </div>

          <div className="flex justify-between items-end pt-4 border-t border-slate-100">
            <div>
              <p className="text-xs text-slate-500">Price</p>
              <p className="font-bold text-lg">₦{(course.price * 1400).toLocaleString()}</p>
            </div>
            <div>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                course.isPublished ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
              }`}>
                {course.isPublished ? 'Published' : 'Draft'}
              </span>
            </div>
          </div>

          {/* Mobile Action Buttons */}
          <div className="flex flex-wrap gap-3 pt-2">
            <Link 
              to={`/admin/courses/${course._id}/edit`}
              className="flex-1 text-center py-3.5 text-sm font-semibold bg-teal-50 text-teal-700 rounded-2xl active:bg-teal-100"
            >
              Edit
            </Link>
            <button
              onClick={() => handleTogglePublish(course._id, course.isPublished)}
              disabled={publishingId === course._id}
              className="flex-1 py-3.5 text-sm font-semibold rounded-2xl transition-all disabled:opacity-70"
            >
              {course.isPublished ? 'Unpublish' : 'Publish'}
            </button>
            <button
              onClick={() => setDeleteConfirm(course._id)}
              className="flex-1 py-3.5 text-sm font-semibold text-rose-600 bg-rose-50 rounded-2xl active:bg-rose-100"
            >
              Delete
            </button>
          </div>
        </div>
      ))}
    </div>

    {/* Empty State */}
    {filtered.length === 0 && (
      <div className="text-center py-20">
        <i className="ri-book-3-line text-6xl text-slate-300 mb-4" />
        <p className="text-slate-600 font-medium">No courses found</p>
        <p className="text-slate-400 text-sm mt-1">Try changing your search or filter</p>
      </div>
    )}
  </div>
</div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
            <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-rose-100 rounded-2xl text-rose-600 text-3xl">
              <i className="ri-delete-bin-6-line" />
            </div>
            
            <h3 className="text-2xl font-bold text-center text-slate-900 mb-2">Delete Course?</h3>
            <p className="text-slate-500 text-center mb-8">
              This action cannot be undone. All associated lessons and data will be permanently deleted.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-3.5 border border-slate-300 rounded-2xl font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={actionLoading}
                className="flex-1 py-3.5 bg-rose-600 hover:bg-rose-700 disabled:bg-rose-400 text-white font-semibold rounded-2xl transition-colors"
              >
                {actionLoading ? 'Deleting...' : 'Yes, Delete Course'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}